import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calculator, FileText, Boxes, LogOut, Loader2, UserPlus, Users, CalendarDays, Megaphone, Receipt, Mail, Cake, DoorOpen, Sparkles, CalendarClock, Trophy, Wrench, Heart, RefreshCw, ShoppingBag, ListChecks } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";

const TILES = [
  { to: "/pos/terminal", icon: Calculator, label: "POS Terminal", desc: "Ring up sales, scan items, finalize transactions.", testid: "pos-tile-terminal" },
  { to: "/pos/transactions", icon: Receipt, label: "Transactions", desc: "Past sales, member attribution, and refunds.", testid: "pos-tile-transactions" },
  { to: "/pos/purchase-requests", icon: ShoppingBag, label: "Purchase Requests", desc: "Customer reservations + unified closed sales ledger.", testid: "pos-tile-purchase-requests" },
  { to: "/pos/wait-list", icon: ListChecks, label: "Wait List", desc: "Customers waiting for restocks — auto-fired emails on match.", testid: "pos-tile-waitlist" },
  { to: "/pos/quotes", icon: FileText, label: "Quotes", desc: "Open & accepted quotes from customers.", testid: "pos-tile-quotes" },
  { to: "/pos/inventory", icon: Boxes, label: "Inventory", desc: "View, add, and remove inventory items.", testid: "pos-tile-inventory" },
  { to: "/pos/members/new", icon: UserPlus, label: "Add Member", desc: "Sign a customer up for AB Member rewards.", testid: "pos-tile-add-member" },
  { to: "/pos/members", icon: Users, label: "Member Lookup", desc: "Search accounts, see spending & adjust µPoints.", testid: "pos-tile-members" },
  { to: "/pos/schedule", icon: CalendarDays, label: "Open Schedule", desc: "Set planned open days — drives the weekly heads-up email.", testid: "pos-tile-schedule" },
  { to: "/pos/events", icon: Megaphone, label: "Events", desc: "Broadcast event emails to all AB Members.", testid: "pos-tile-events" },
];

export default function POSDashboard() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(""); // which email job is running
  const [emailMsg, setEmailMsg] = useState("");
  const [healing, setHealing] = useState(false);
  const [healMsg, setHealMsg] = useState("");

  useEffect(() => {
    api.get("/store/status")
      .then((r) => setIsOpen(!!r.data.is_open))
      .finally(() => setStatusLoading(false));
  }, []);

  const toggle = async (checked) => {
    setSaving(true);
    setIsOpen(checked); // optimistic
    try {
      await api.post("/store/status", { is_open: checked });
    } catch {
      setIsOpen(!checked); // rollback
    } finally {
      setSaving(false);
    }
  };

  const runEmailJob = async (key, path, label) => {
    setRunning(key); setEmailMsg("");
    try {
      const r = await api.post(path);
      if (r.data?.new_arrivals != null) {
        setEmailMsg(`${label}: ${r.data.sent} email(s) queued (${r.data.new_arrivals} new arrival item(s) × ${r.data.members} member(s))`);
      } else {
        setEmailMsg(`${label}: dispatched. Check Mongo / Resend for delivery details.`);
      }
    } catch (e) {
      setEmailMsg(`${label} failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning("");
    }
  };

  const logout = () => {
    auth.clear();
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Back Office</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black">Control Room</h1>
          </div>
          <button onClick={logout} className="btn-secondary inline-flex items-center gap-2" data-testid="pos-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>

        {/* Storefront status toggle */}
        <div className="surface p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="store-status-card">
          <div className="flex-1 min-w-[220px]">
            <div className="label-eyebrow mb-1" style={{ color: "var(--ab-primary)" }}>Storefront Status</div>
            <div className="font-display text-xl font-bold flex items-center gap-3">
              {statusLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : isOpen ? (
                <>
                  <span className="open-now-dot" />
                  <span style={{ color: "#4ADE80" }}>Open — visible to public</span>
                </>
              ) : (
                <>
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: "var(--ab-text-dim)" }} />
                  <span style={{ color: "var(--ab-text-muted)" }}>Closed — hidden from public</span>
                </>
              )}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--ab-text-dim)" }}>
              Toggle on when the popup store is open. A neon "OPEN NOW" indicator appears in every public header.
            </p>
          </div>
          <label className="toggle-switch" data-testid="store-status-toggle">
            <input
              type="checkbox"
              checked={isOpen}
              disabled={saving || statusLoading}
              onChange={(e) => toggle(e.target.checked)}
              data-testid="store-status-toggle-input"
            />
            <span className="toggle-slider" />
          </label>
        </div>

        {/* Email Tools — manual triggers for the scheduler jobs */}
        <details className="surface mb-6" data-testid="email-tools-panel">
          <summary className="px-6 py-4 flex items-center gap-3 cursor-pointer select-none">
            <Mail size={18} style={{ color: "var(--ab-primary)" }} />
            <div className="flex-1">
              <div className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Email Tools</div>
              <div className="font-display text-lg font-bold">Manual Broadcasts</div>
            </div>
            <span className="text-xs" style={{ color: "var(--ab-text-dim)" }}>Click to expand ▾</span>
          </summary>
          <div className="px-6 pb-6 pt-2 border-t" style={{ borderColor: "var(--ab-border)" }}>
            <p className="text-xs mb-4" style={{ color: "var(--ab-text-muted)" }}>
              These run on a schedule automatically (Fridays 12:01 CST and daily 00:05 CST). Use these buttons to fire them on demand for testing or one-off blasts.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <EmailJobBtn
                running={running === "bday"}
                disabled={!!running}
                onClick={() => runEmailJob("bday", "/admin/run-member-maintenance?force_birthday=true", "Birthday µPoints")}
                icon={Cake}
                label="Birthday µPoints"
                desc="Auto: 1st of month, 00:05 CST. Grants 2,500 µPts to members whose birth month = current."
                testid="email-job-birthday"
              />
              <EmailJobBtn
                running={running === "top"}
                disabled={!!running}
                onClick={() => runEmailJob("top", "/admin/run-member-maintenance?force_top_spender=true", "Top Spender")}
                icon={Trophy}
                label="Top Spender (1-3)"
                desc="Auto: 1st of month. Up to 3 emails to prev-month top spenders ≥ $200."
                testid="email-job-top-spender"
              />
              <EmailJobBtn
                running={running === "sched"}
                disabled={!!running}
                onClick={() => runEmailJob("sched", "/admin/run-schedule-diff?force=true", "Schedule Update")}
                icon={CalendarClock}
                label="Schedule Update"
                desc="Auto: Monday 09:00 CST. Emails members only if open-schedule has changed since last Monday."
                testid="email-job-schedule"
              />
              <EmailJobBtn
                running={running === "inv"}
                disabled={!!running}
                onClick={() => runEmailJob("inv", "/admin/run-weekly-inventory-email?days=7", "New Arrivals")}
                icon={Sparkles}
                label="Weekly New Arrivals"
                desc="Auto: Tuesday 00:01 CST after catalog sync. Emails the last 7 days of new inventory."
                testid="email-job-inventory"
              />
              <EmailJobBtn
                running={running === "promo"}
                disabled={!!running}
                onClick={() => runEmailJob("promo", "/admin/rotate-promo", "Bonus µPoint Promo")}
                icon={Megaphone}
                label="Bonus µPoint Promo"
                desc="Auto: Wednesday 09:00 CST. Picks 5 random items ≥$10 and emails the promo."
                testid="email-job-promo"
              />
              <EmailJobBtn
                running={running === "open"}
                disabled={!!running}
                onClick={() => runEmailJob("open", "/admin/run-member-maintenance?force_opening=true", "We're Open Reminder")}
                icon={DoorOpen}
                label="We're Open This Week"
                desc="Auto: daily 00:05 CST, fires 24h before each scheduled open day (Thu/Fri/Sat)."
                testid="email-job-opening"
              />
              <EmailJobBtn
                running={running === "evt"}
                disabled={!!running}
                onClick={() => runEmailJob("evt", "/admin/run-member-maintenance?force_events=true", "Pending Events")}
                icon={Megaphone}
                label="Pending Events"
                desc="Auto: daily 00:05 CST when send_email_at ≤ now. Sends all queued event emails."
                testid="email-job-events"
              />
            </div>
            {emailMsg && (
              <div className="mt-4 text-xs px-4 py-3" style={{ background: "var(--ab-surface-2)", border: "1px solid var(--ab-border)", color: "var(--ab-text)" }} data-testid="email-job-msg">
                {emailMsg}
              </div>
            )}
          </div>
        </details>

        {/* Maintenance — destructive / data-healing tools */}
        <details className="surface mb-6" data-testid="maintenance-panel">
          <summary className="px-6 py-4 flex items-center gap-3 cursor-pointer select-none">
            <Wrench size={18} style={{ color: "var(--ab-primary)" }} />
            <div className="flex-1">
              <div className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Maintenance</div>
              <div className="font-display text-lg font-bold">Data Healing &amp; One-Off Jobs</div>
            </div>
            <span className="text-xs" style={{ color: "var(--ab-text-dim)" }}>Click to expand ▾</span>
          </summary>
          <div className="px-6 pb-6 pt-2 border-t" style={{ borderColor: "var(--ab-border)" }}>
            <p className="text-xs mb-4" style={{ color: "var(--ab-text-muted)" }}>
              Manual tools for cleaning up bad data. Safe to run repeatedly.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <EmailJobBtn
                running={healing}
                disabled={healing}
                onClick={async () => {
                  setHealing(true); setHealMsg("");
                  try {
                    const r = await api.post("/admin/heal-catalog");
                    setHealMsg(`Heal Catalog: removed ${r.data.removed_count} poisoned entries.`);
                  } catch (e) {
                    setHealMsg(`Heal Catalog failed: ${e.response?.data?.detail || e.message}`);
                  } finally { setHealing(false); }
                }}
                icon={Heart}
                label="Heal Catalog"
                desc="Removes poisoned PriceCharting catalog entries (title_key mismatched with title). Run this once after deploying barcode fixes."
                testid="maintenance-heal-catalog"
              />
              <EmailJobBtn
                running={false}
                disabled={false}
                onClick={async () => {
                  setHealing(true); setHealMsg("");
                  try {
                    const r = await api.post("/admin/queue-barcode-backfill");
                    setHealMsg(`Barcode backfill queued — will run on the next Tuesday sync. (pending=${r.data.pending})`);
                  } catch (e) {
                    setHealMsg(`Failed: ${e.response?.data?.detail || e.message}`);
                  } finally { setHealing(false); }
                }}
                icon={Sparkles}
                label="Queue Barcode Backfill"
                desc="Arms the one-time barcode backfill for next Tuesday's sync. Fills missing UPCs on all inventory + catalog."
                testid="maintenance-queue-backfill"
              />
              <EmailJobBtn
                running={false}
                disabled={false}
                onClick={async () => {
                  setHealing(true); setHealMsg("");
                  try {
                    const r = await api.post("/admin/run-barcode-backfill-now?wait=true");
                    const s = r.data?.summary || {};
                    setHealMsg(
                      `Backfill done — catalog refreshed: ${s.catalog_updated ?? 0} (still missing UPC: ${s.catalog_fallback ?? 0}), inventory rows updated: ${s.inventory_updated ?? 0}, unchanged: ${s.inventory_unchanged ?? 0}, relinked to sibling catalog: ${s.inventory_catalog_relinked ?? 0}, auto-assigned unique SKU: ${s.inventory_sku_assigned ?? 0}, still on 123456 fallback: ${s.inventory_fallback ?? 0}.`
                    );
                  } catch (e) {
                    // wait=true can exceed proxy timeout on large catalogs — fall back to background mode.
                    try {
                      await api.post("/admin/run-barcode-backfill-now");
                      setHealMsg("Backfill is taking a while — kicked off in the background. Refresh Inventory in a few minutes.");
                    } catch (e2) {
                      setHealMsg(`Failed: ${e2.response?.data?.detail || e2.message}`);
                    }
                  } finally { setHealing(false); }
                }}
                icon={Wrench}
                label="Run Backfill Now"
                desc="Repairs inventory rows whose barcode is missing OR still on the '123456' fallback by pulling the real UPC from each row's linked catalog entry. Waits up to ~100s and shows a summary."
                testid="maintenance-run-backfill"
              />
              <EmailJobBtn
                running={false}
                disabled={false}
                onClick={async () => {
                  setHealing(true); setHealMsg("");
                  try {
                    await api.post("/admin/run-nightly");
                    setHealMsg("Weekly Inventory Sync kicked off in the background. PriceCharting is rate-limited so this can take several minutes — you'll get the summary email when it's done.");
                  } catch (e) {
                    setHealMsg(`Failed: ${e.response?.data?.detail || e.message}`);
                  } finally { setHealing(false); }
                }}
                icon={RefreshCw}
                label="Run Weekly Sync Now"
                desc="Manually triggers the Tuesday catalog refresh: re-scrapes PriceCharting for every catalog entry, re-prices in-stock inventory on ≥10% moves, and emails you the summary scoped to items you currently hold."
                testid="maintenance-run-weekly-sync"
              />
              <EmailJobBtn
                running={false}
                disabled={false}
                onClick={async () => {
                  setHealing(true); setHealMsg("");
                  try {
                    await api.post("/admin/run-weekly-promo");
                    setHealMsg("Weekly Promo rotation kicked off in the background. Members will receive the Bonus µPoint email shortly.");
                  } catch (e) {
                    setHealMsg(`Failed: ${e.response?.data?.detail || e.message}`);
                  } finally { setHealing(false); }
                }}
                icon={Sparkles}
                label="Run Weekly Promo Now"
                desc="Manually rotates the bonus-µPoint promo titles and emails members. Use this if the Wednesday 9 AM cron missed a run (e.g. after a redeploy)."
                testid="maintenance-run-weekly-promo"
              />
            </div>
            {healMsg && (
              <div className="mt-4 text-xs px-4 py-3" style={{ background: "var(--ab-surface-2)", border: "1px solid var(--ab-border)", color: "var(--ab-text)" }} data-testid="maintenance-msg">
                {healMsg}
              </div>
            )}
          </div>
        </details>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.to} to={t.to} className="surface p-6 hover:bg-[var(--ab-surface-2)] transition-colors" data-testid={t.testid}>
                <Icon size={26} strokeWidth={1.5} style={{ color: "var(--ab-primary)" }} />
                <div className="mt-4 font-display text-xl font-bold">{t.label}</div>
                <div className="text-xs mt-2" style={{ color: "var(--ab-text-muted)" }}>{t.desc}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}

function EmailJobBtn({ running, disabled, onClick, icon: Icon, label, desc, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-left p-4 transition-colors"
      style={{
        border: "1px solid var(--ab-border)",
        background: "var(--ab-surface-2)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !running ? 0.5 : 1,
      }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {running ? <Loader2 size={16} className="animate-spin" style={{ color: "var(--ab-primary)" }} /> : <Icon size={16} style={{ color: "var(--ab-primary)" }} />}
        <span className="font-display font-bold text-sm">{label}</span>
      </div>
      <div className="text-[11px] mt-2" style={{ color: "var(--ab-text-dim)", lineHeight: 1.5 }}>{desc}</div>
    </button>
  );
}
